import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Stack,
  Button,
  Chip,
  Avatar,
  Divider,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Fade,
  Zoom
} from '@mui/material';
import {
  Business as BusinessIcon,
  LocationOn as LocationOnIcon,
  Description as DescriptionIcon,
  VideoLibrary as VideoLibraryIcon,
  PhotoLibrary as PhotoLibraryIcon,
  AttachMoney as AttachMoneyIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Visibility as VisibilityIcon,
  Download as DownloadIcon
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { GetAll, Post, apiURL } from '../../../utils/apiCalls';
import { toast } from 'react-toastify';

const LayoutRequestApproval = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [request, setRequest] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rejectionModalOpen, setRejectionModalOpen] = useState(false);
  const [rejectionData, setRejectionData] = useState({
    comments: '',
    admin_notes: ''
  });

  const fetchRequestDetails = useCallback(async () => {
    setLoading(true);
    try {
      const response = await GetAll(apiURL + `/api/v1/layouts/${id}`);
      if (response && response.data && response.data.status) {
        setRequest(response.data.data);
      } else {
        toast.error('Failed to fetch request details');
        navigate('/dashboard/layout-requests');
      }
    } catch (error) {
      console.error('Error fetching request details:', error);
      toast.error('An error occurred while fetching request details');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    fetchRequestDetails();
  }, [fetchRequestDetails]);

  useEffect(() => {
    try {
      const storedUser = sessionStorage.getItem('user_details');
      if (storedUser) {
        setCurrentUser(JSON.parse(storedUser));
      }
    } catch (e) {
      console.error('Error parsing user_details from sessionStorage', e);
    }
  }, []);

  const canApprove =
    currentUser &&
    (
      currentUser.user_type === 'admin' ||
      (currentUser.user_type === 'Procurement' && currentUser?.role?.designation === 'Procurement Director')
    );

  const handleApproval = async () => {
    try {
      const response = await Post(apiURL + '/api/v1/layouts/approve_request', {
        id: id,
        status: 'approved'
      });
      if (response && response.data && response.data.status) {
        toast.success('Request approved successfully');
        navigate('/dashboard/layout-requests');
      } else {
        toast.error(response?.data?.message || 'Failed to approve request');
      }
    } catch (error) {
      console.error('Error approving request:', error);
      toast.error('An error occurred while approving request');
    }
  };

  const handleRejection = async () => {
    try {
      const response = await Post(apiURL + '/api/v1/layouts/reject_request', {
        id: id,
        ...rejectionData
      });
      if (response && response.data && response.data.status) {
        toast.success('Request rejected successfully');
        setRejectionModalOpen(false);
        navigate('/dashboard/layout-requests');
      } else {
        toast.error(response?.data?.message || 'Failed to reject request');
      }
    } catch (error) {
      console.error('Error rejecting request:', error);
      toast.error('An error occurred while rejecting request');
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'approved': return 'success';
      case 'rejected': return 'error';
      case 'pending': return 'warning';
      default: return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'approved': return <CheckCircleIcon />;
      case 'rejected': return <CancelIcon />;
      case 'pending': return <CircularProgress size={20} />;
      default: return null;
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
          <CircularProgress size={64} />
        </Box>
      </Container>
    );
  }

  if (!request) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">Request not found</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Fade in timeout={800}>
        <Box sx={{ mb: 4 }}>
          <Stack direction="row" alignItems="center" spacing={3} sx={{ mb: 3 }}>
            <Avatar sx={{
              bgcolor: '#1976d2',
              width: 64,
              height: 64,
              boxShadow: '0 8px 16px rgba(25, 118, 210, 0.3)'
            }}>
              <BusinessIcon sx={{ fontSize: 32, color: 'white' }} />
            </Avatar>
            <Box>
              <Typography variant="h4" component="h1" fontWeight="bold" color="text.primary">
                Layout Request Approval
              </Typography>
              <Typography variant="h6" color="text.secondary">
                Review and approve layout onboarding request
              </Typography>
            </Box>
          </Stack>
          <Divider sx={{ my: 2 }} />
        </Box>
      </Fade>

      <Grid container spacing={4}>
        {/* Request Details */}
        <Grid item xs={12} md={8}>
          <Stack spacing={4}>
            {/* Venture Information */}
            <Zoom in timeout={1000}>
              <Card>
                <CardContent>
                  <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
                    <BusinessIcon color="primary" />
                    <Typography variant="h6" fontWeight="bold">
                      Venture Information
                    </Typography>
                  </Stack>
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">Venture Name</Typography>
                      <Typography variant="body1" fontWeight="bold">
                        {request.venture_name}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">Company Name</Typography>
                      <Typography variant="body1" fontWeight="bold">
                        {request.company_name}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Zoom>

            {/* Location Information */}
            <Zoom in timeout={1200}>
              <Card>
                <CardContent>
                  <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
                    <LocationOnIcon color="primary" />
                    <Typography variant="h6" fontWeight="bold">
                      Location Information
                    </Typography>
                  </Stack>
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">Latitude</Typography>
                      <Typography variant="body1" fontWeight="bold">
                        {request.location_latitude}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">Longitude</Typography>
                      <Typography variant="body1" fontWeight="bold">
                        {request.location_longitude}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Zoom>

            {/* Legal Documents */}
            <Zoom in timeout={1400}>
              <Card>
                <CardContent>
                  <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
                    <DescriptionIcon color="primary" />
                    <Typography variant="h6" fontWeight="bold">
                      Legal Documents
                    </Typography>
                  </Stack>
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={4}>
                      <Typography variant="body2" color="text.secondary">LP Number</Typography>
                      <Typography variant="body1" fontWeight="bold">
                        {request.lp_number}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Typography variant="body2" color="text.secondary">HMDA Letter</Typography>
                      <Typography variant="body1" fontWeight="bold">
                        {request.hmda_letter}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Typography variant="body2" color="text.secondary">REPA Number</Typography>
                      <Typography variant="body1" fontWeight="bold">
                        {request.repa_number}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Zoom>

            {/* Commission Information */}
            <Zoom in timeout={1600}>
              <Card>
                <CardContent>
                  <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
                    <AttachMoneyIcon color="primary" />
                    <Typography variant="h6" fontWeight="bold">
                      Commission Information
                    </Typography>
                  </Stack>
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">Associate Highest Commission</Typography>
                      <Typography variant="body1" fontWeight="bold">
                        {request.associate_highest_commission}%
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">PLP Margin</Typography>
                      <Typography variant="body1" fontWeight="bold">
                        {request.plp_margin}%
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Zoom>

            {/* Documents and Media */}
            <Zoom in timeout={1800}>
              <Card>
                <CardContent>
                  <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
                    <PhotoLibraryIcon color="primary" />
                    <Typography variant="h6" fontWeight="bold">
                      Documents and Media
                    </Typography>
                  </Stack>
                  <Grid container spacing={2}>
                    {request.documents && request.documents.length > 0 ? (
                      request.documents.map((doc, index) => (
                        <Grid item xs={12} sm={6} md={4} key={doc.id || index}>
                          <Button
                            variant="outlined"
                            startIcon={
                              doc.mime_type?.startsWith('image/') ? (
                                <PhotoLibraryIcon />
                              ) : doc.mime_type?.startsWith('video/') ? (
                                <VideoLibraryIcon />
                              ) : (
                                <DownloadIcon />
                              )
                            }
                            fullWidth
                            onClick={() => window.open(doc.s3_url, '_blank')}
                            sx={{ mb: 1 }}
                          >
                            <Typography variant="body2" noWrap>
                              {doc.sub_category 
                                ? doc.sub_category.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
                                : doc.document_type?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
                              }
                            </Typography>
                          </Button>
                          <Typography variant="caption" color="text.secondary" display="block">
                            {(doc.file_size / 1024 / 1024).toFixed(2)} MB
                          </Typography>
                        </Grid>
                      ))
                    ) : (
                      <Grid item xs={12}>
                        <Typography color="text.secondary">
                          No documents uploaded
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                </CardContent>
              </Card>
            </Zoom>
          </Stack>
        </Grid>

        {/* Approval Panel */}
        <Grid item xs={12} md={4}>
          <Zoom in timeout={2000}>
            <Paper elevation={3} sx={{ p: 3, position: 'sticky', top: 20 }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Request Status
              </Typography>
              <Chip
                label={request.status || 'Pending'}
                color={getStatusColor(request.status)}
                icon={getStatusIcon(request.status)}
                sx={{ mb: 3, fontWeight: 'bold' }}
              />

              <Divider sx={{ my: 3 }} />

              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Actions
              </Typography>
              <Stack spacing={2}>
              {canApprove && (
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<CheckCircleIcon />}
                  onClick={() => {
                    if (window.confirm('Are you sure you want to approve this layout request?')) {
                      handleApproval();
                    }
                  }}
                  disabled={request.status === 'approved' || request.status === 'rejected'}
                  fullWidth
                >
                  Approve Request
                </Button>
              )}
                <Button
                  variant="contained"
                  color="error"
                  startIcon={<CancelIcon />}
                  onClick={() => setRejectionModalOpen(true)}
                  disabled={request.status === 'approved' || request.status === 'rejected'}
                  fullWidth
                >
                  Reject Request
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<VisibilityIcon />}
                  onClick={() => navigate('/dashboard/layout-requests')}
                  fullWidth
                >
                  Back to List
                </Button>
              </Stack>

              {request.admin_notes && (
                <>
                  <Divider sx={{ my: 3 }} />
                  <Typography variant="h6" fontWeight="bold" gutterBottom>
                    Admin Notes
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {request.admin_notes}
                  </Typography>
                </>
              )}
            </Paper>
          </Zoom>
        </Grid>
      </Grid>


      {/* Rejection Modal */}
      <Dialog open={rejectionModalOpen} onClose={() => setRejectionModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Reject Layout Request</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <TextField
              fullWidth
              label="Rejection Reason"
              multiline
              rows={4}
              value={rejectionData.comments}
              onChange={(e) => setRejectionData({ ...rejectionData, comments: e.target.value })}
              required
            />
            <TextField
              fullWidth
              label="Admin Notes"
              multiline
              rows={3}
              value={rejectionData.admin_notes}
              onChange={(e) => setRejectionData({ ...rejectionData, admin_notes: e.target.value })}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectionModalOpen(false)}>Cancel</Button>
          <Button onClick={handleRejection} variant="contained" color="error">
            Reject
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default LayoutRequestApproval;
